import React, { useContext } from "react";
import "../styles/aboutUs.css";
import { WebsiteContext } from "../context/AppContext";
import HelmetComponent from "../components/common/HelmetComponent";
import { useNavigate } from "react-router-dom";
import { HiSparkles, HiLightBulb, HiUserGroup, HiAcademicCap } from "react-icons/hi2";
import { IoCheckmarkCircle, IoCalendarOutline } from "react-icons/io5";
import { FiArrowRight } from "react-icons/fi";
import { motion } from "framer-motion";

const aboutStories = [
  {
    icon: <HiLightBulb />,
    iconClass: "icon_blue",
    title: "Our Mission",
    desc: "We believe fluency is not an exclusive privilege, but an achievable journey for everyone. We eliminate the fear of making mistakes by giving learners a friendly, judgement-free space to speak daily.",
  },
  {
    icon: <HiSparkles />,
    iconClass: "icon_purple",
    title: "AI-Powered Pedagogy",
    desc: "From Daily AI Insights with instant synonyms & antonyms to 24/7 conversational practice, we bring intelligent, personalized feedback right into your pocket.",
  },
  {
    icon: <HiUserGroup />,
    iconClass: "icon_amber",
    title: "Vibrant Community",
    desc: "With 270,000+ passionate learners across YouTube, Instagram, and the Fluent Fable app, you are never learning alone. Connect, practice, and celebrate language milestones together.",
  },
];

const masterclassPerks = [
  "Monday to Friday | Live Interactive Sessions on Google Meet",
  "1 Hour Daily of Personalized, Real-World Conversation Practice",
  "Special Focus on Pronunciation, Accent Cadence & Confidence",
  "Exclusive WhatsApp Learner Group for Daily English Drills",
  "Real-Time Corrections & Tailored Feedback Every Session",
  "1-Year Ongoing Mentor Support Even After the Course Ends",
  "Perfect for Students, Working Professionals & Job Seekers",
  "No Pre-recorded Videos — 100% Live Practice & Interaction",
];

const AboutUs = () => {
  const { color, dark } = useContext(WebsiteContext);
  const navigate = useNavigate();

  const handleEnroll = () => {
    navigate("/razorpay/1");
  };

  return (
    <>
      <HelmetComponent
        title="About Us | Fluent Fable"
        name="description"
        content="Learn about Fluent Fable's mission to empower global English learners through interactive AI practice, structured learning paths, and live spoken English masterclasses."
      />

      <div className="about_page_wrapper">
        {/* Hero */}
        <div className="about_hero_section">
          <span className={`about_badge ${dark ? "about_badge_dark" : "about_badge_light"}`}>
            💡 About Fluent Fable
          </span>
          <h1 className="about_hero_title" style={{ color: color }}>
            Empowering Voices, Transforming English Fluency
          </h1>
          <p className="about_hero_subtitle" style={{ color: color }}>
            We bridge the gap between classroom theory and spontaneous conversational fluency through intelligent AI practice, daily vocabulary insights, and high-impact live mentorship.
          </p>
        </div>

        {/* Story Grid */}
        <div className="about_story_grid">
          {aboutStories.map((story, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`about_story_card ${dark ? "about_card_dark" : "about_card_light"}`}
            >
              <div className={`story_card_icon ${story.iconClass}`}>{story.icon}</div>
              <h2 className="story_card_title" style={{ color: color }}>
                {story.title}
              </h2>
              <p className="story_card_desc" style={{ color: color }}>
                {story.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Live Mastery Program Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className={`masterclass_card_banner ${dark ? "banner_dark" : "banner_light"}`}
        >
          <div className="masterclass_top_row">
            <span className="masterclass_tag">
              <HiAcademicCap size={15} /> Intensive Mentorship Program
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.8, display: "flex", alignItems: "center", gap: 6 }}>
              <IoCalendarOutline size={15} /> 1-Month Duration
            </span>
          </div>

          <h2 className="masterclass_title">
            1-Month Spoken English Live Mastery (1 Hour / Day)
          </h2>

          <div className="masterclass_perks_grid">
            {masterclassPerks.map((perk, i) => (
              <div key={i} className="perk_item">
                <IoCheckmarkCircle size={18} className="perk_check" />
                <span>{perk}</span>
              </div>
            ))}
          </div>

          <div className="masterclass_action_row">
            <div className="pricing_group">
              <span className="price_main">₹4,000</span>
              <span className="price_sub">All-inclusive fee for 30 days of live training</span>
            </div>

            <button className="enroll_btn" onClick={handleEnroll}>
              <span>Enroll in Live Program</span>
              <FiArrowRight size={17} />
            </button>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default AboutUs;